import CropIcon from '@assets/icons/CropIcon';
import { cn } from '@components/shadcn/utils';
import CustomSkeleton from '@components/shared/CustomSkeleton';
import { useProperty } from '@hooks/queries/useProperty';
import { routePaths } from '@screens/routes';
import { selectSeason } from '@state/season/season.selectors';
import { useBoundStore } from '@state/store';
import { Tooltip } from 'antd';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import styles from './_seasonPopoverTitle.module.scss';

export function SeasonPopoverTitle() {
  const { status, mode } = useBoundStore((state) => state.seasons);
  const selectedSeason = useBoundStore(selectSeason);
  const { property } = useProperty();
  const [params] = useSearchParams();

  const { t } = useTranslation();

  const [tooltipVisible, setTooltipVisible] = useState(false);

  const isOnYieldAnalysis = useMatch({ path: routePaths.yieldAnalysis, end: false });
  const isOnAnomalyDetectionSettings = params.get('settingsModal') === 'AnomalyDetection';

  const formatSeasonYear = (starDate: string, endDate: string): string => {
    return `${new Date(starDate).getFullYear().toString().slice(-2)} - ${new Date(endDate)
      .getFullYear()
      .toString()
      .slice(-2)}`;
  };

  const title = useMemo(() => {
    if (mode === 'active_seasons') {
      return (
        <span className="typo typo--paragraph-1 typo--bold typo--primary typo--nowrap">
          {t('header.seasons.active_seasons_title')}
        </span>
      );
    }

    if (selectedSeason) {
      const { season } = selectedSeason;

      return (
        <span className="typo typo--paragraph-1 typo--bold typo--nowrap">
          {formatSeasonYear(season.start_date, season.end_date)}
        </span>
      );
    }

    if (isOnYieldAnalysis) {
      return (
        <span className="typo typo--paragraph-1 typo--bold typo--primary typo--nowrap text-sy-neutral-40">
          {t('header.seasons.yield_analysis.empty.title')}
        </span>
      );
    }

    return (
      <span className="typo typo--paragraph-1 typo--bold typo--primary typo--nowrap text-sy-neutral-40">
        {t('header.seasons.no_selected_season.title')}
      </span>
    );
  }, [mode, selectedSeason, isOnYieldAnalysis, t]);

  if (status === 'loading' || !property) {
    return <CustomSkeleton height={20} width={120} />;
  }

  return (
    <Tooltip
      visible={Boolean(
        tooltipVisible && isOnYieldAnalysis && mode === 'unique_season' && !selectedSeason
      )}
      onVisibleChange={(visible) => {
        if (isOnYieldAnalysis) {
          setTooltipVisible(visible);
        }
      }}
      overlayClassName={styles.tooltip}
      arrowPointAtCenter
      placement="bottomLeft"
      title={t('yield_analysis.empty_state.tooltip_message')}
    >
      <div className={'flex items-center'} data-testid="season-popover-title">
        <div
          className={cn('flex [&>svg>path]:fill-sy-green-60', {
            ['[&>svg>path]:fill-sy-neutral-40']:
              (isOnYieldAnalysis && mode === 'unique_season' && !selectedSeason) ||
              isOnAnomalyDetectionSettings,
          })}
        >
          <CropIcon
            crop={selectedSeason?.season.catalogCrop?.adapted_wk_slug ?? 'wheat'}
            width={18}
            height={18}
          />
        </div>
        <span>{title}</span>
      </div>
    </Tooltip>
  );
}
